import { useState, useCallback, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { AuthService } from "../services/authService";
import { baseApiService } from "../services/baseApiService";
import { TokenService } from "../services/tokenService";

export const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  // MsalProvider always seeds accounts as [] on mount and only populates it once
  // handleRedirectPromise resolves - until then, inProgress stays "startup". Treating
  // that transient window as "logged out" wipes any session-restored state too early
  // (e.g. persisted UI selections) before MSAL has had a chance to report the real
  // authentication state on a page refresh.
  const isAuthResolving = inProgress === InteractionStatus.Startup;
  const [authService] = useState(() => {
    const service = new AuthService(instance);
    // Set the auth service instance on the base API service
    baseApiService.setAuthService(service);
    return service;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userAccountError, setUserAccountError] = useState<{
    type: "notFound" | "inactive";
    email?: string;
  } | null>(null);
  const [backendToken, setBackendToken] = useState<string | null>(null);
  const [authFlowCompleted, setAuthFlowCompleted] = useState(false);

  // Check if user is authenticated
  const isAuthenticated = accounts.length > 0;
  const currentAccount = accounts.length > 0 ? accounts[0] : null;

  // Set authFlowCompleted to true for page refresh scenarios where user is already authenticated
  useEffect(() => {
    if (isAuthenticated && !authFlowCompleted) {
      // Only auto-complete auth flow if we have a stored token or if not on login page
      const currentPath = window.location.pathname;
      const isOnLoginPage = currentPath === "/login";
      const storedToken = localStorage.getItem("bestekJwtToken");

      if (!isOnLoginPage && storedToken) {
        // Always validate token is not expired before using it
        const isExpired = TokenService.isTokenExpired(storedToken);
        
        if (isExpired) {
          console.log("useAuth: Stored token is expired, clearing it");
          localStorage.removeItem("bestekJwtToken");
          // Don't auto-complete, need fresh token
          return;
        }
        
        // User is authenticated, has valid stored token, and not on login page - safe to mark as completed
        const timer = setTimeout(() => {
          if (!authFlowCompleted) {
            console.log(
              "useAuth: Auto-completing auth flow for page refresh with valid stored token"
            );
            setAuthFlowCompleted(true);
          }
        }, 300); // Longer delay to prevent race conditions

        return () => clearTimeout(timer);
      }
    }
  }, [isAuthenticated, authFlowCompleted]);

  // Monitor authentication state changes
  useEffect(() => {
    const loginInProgress =
      sessionStorage.getItem("login-in-progress") === "true";

    // If login is marked as in progress but we have no authentication after some time,
    // clear the flag to prevent getting stuck
    if (loginInProgress && !isAuthenticated && !isLoading) {
      const loginStartTime = sessionStorage.getItem("login-start-time");
      const now = Date.now();
      const timeSinceLogin = loginStartTime
        ? now - parseInt(loginStartTime)
        : 0;

      // If more than 30 seconds have passed since login started, clear the flag
      if (timeSinceLogin > 30000) {
        console.warn(
          "useAuth: Login has been in progress too long, clearing flags"
        );
        sessionStorage.removeItem("login-in-progress");
        sessionStorage.removeItem("login-start-time");
        sessionStorage.removeItem("auth-method");
      }
    }
  }, [
    accounts.length,
    isAuthenticated,
    currentAccount,
    backendToken,
    isLoading,
  ]);

  // Login with Microsoft SSO and exchange for backend token
  const login = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // First, login with Microsoft (always uses redirect flow now)
      const loginResponse = await authService.login();

      // For redirect flow, login() returns null and redirects the page
      // The actual authentication completion is handled after redirect
      if (loginResponse === null) {
        // This is a redirect flow - the page will be redirected
        // Don't try to exchange tokens here as the authentication is not complete yet
        return null;
      }

      // If we get here, there was an unexpected response
      // (since redirect flow should always return null)
      console.warn("Unexpected login response:", loginResponse);
      const token = await authService.exchangeTokenWithBackend();

      setBackendToken(token);

      // Store the token for API calls
      localStorage.setItem("bestekJwtToken", token);

      return token;
    } catch (err) {
      console.error("Login error:", err);
      const errorMessage = err instanceof Error ? err.message : "Login failed";

      // Handle interaction_in_progress errors gracefully
      if (
        errorMessage.includes("interaction_in_progress") ||
        errorMessage.includes("Authentication interaction in progress")
      ) {
        console.log(
          "useAuth: Authentication interaction already in progress, waiting..."
        );
        setError(null); // Don't show error for this case
        setIsLoading(false);
        return null;
      }

      setError(errorMessage);

      // For now, let's continue even if backend exchange fails
      // This allows testing the frontend without backend
      if (errorMessage.includes("Token exchange failed")) {
        console.warn(
          "Backend token exchange failed, continuing with Azure token only"
        );
        // You could set a mock token here for development
        // setBackendToken('mock-token-for-development');
        // localStorage.setItem("bestekJwtToken", 'mock-token-for-development');
      }

      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [authService]);

  // Logout
  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Set logout flag to prevent redirect issues
      sessionStorage.setItem("logout-in-progress", "true");

      // Clear all application states
      setBackendToken(null);
      setUserAccountError(null);
      setAuthFlowCompleted(false);
      setError(null);

      // Call authService logout (which will clear all storage)
      await authService.logout();

      console.log("useAuth: Logout completed successfully");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Logout failed";
      console.error("useAuth: Logout failed:", err);
      setError(errorMessage);

      // Even if logout fails, still clear local states and storage
      setBackendToken(null);
      setUserAccountError(null);
      setAuthFlowCompleted(false);

      // Force clear storage as fallback
      try {
        localStorage.clear();
        sessionStorage.clear();
        console.log("useAuth: Storage cleared as fallback after logout error");
      } catch (storageError) {
        console.error(
          "useAuth: Failed to clear storage as fallback:",
          storageError
        );
      }

      throw err;
    } finally {
      setIsLoading(false);

      // Clear logout flag after a short delay
      setTimeout(() => {
        sessionStorage.removeItem("logout-in-progress");
      }, 100);
    }
  }, [authService]);

  // Get backend token
  const getBackendToken = useCallback(async () => {
    if (!isAuthenticated) {
      throw new Error("User not authenticated");
    }

    try {
      setIsLoading(true);
      const token = await authService.exchangeTokenWithBackend();
      setBackendToken(token);
      localStorage.setItem("bestekJwtToken", token);
      setIsLoading(false);
      setAuthFlowCompleted(true);
      return token;
    } catch (err) {
      setIsLoading(false);
      setAuthFlowCompleted(true);
      setError(
        err instanceof Error ? err.message : "Failed to get backend token"
      );
      throw err;
    }
  }, [authService, isAuthenticated]);

  // Initialize backend token on mount if user is already authenticated
  useEffect(() => {
    if (isAuthenticated && !backendToken && !authFlowCompleted) {
      const storedToken = localStorage.getItem("bestekJwtToken");
      
      // Check if stored token is valid and not expired
      let shouldUseStoredToken = false;
      if (storedToken) {
        const isExpired = TokenService.isTokenExpired(storedToken);
        if (!isExpired) {
          shouldUseStoredToken = true;
        } else {
          console.log("useAuth: Stored token is expired, will fetch new one");
          localStorage.removeItem("bestekJwtToken");
        }
      }
      
      if (shouldUseStoredToken && storedToken) {
        setBackendToken(storedToken);
        setAuthFlowCompleted(true); // Mark as completed when using stored token
        console.log(
          "useAuth: Using stored token and marking auth as completed"
        );
      } else {
        // Try to get fresh backend token after a short delay
        // This gives time for the authentication state to fully settle
        const timer = setTimeout(() => {
          console.log("useAuth: Starting backend token exchange...");
          getBackendToken().catch((error) => {
            console.error("useAuth: Backend token exchange failed:", error);
            console.log("useAuth: Error name:", error.name);
            console.log("useAuth: Error message:", error.message);

            // Handle specific user account errors
            if (error.name === "UserNotFoundError") {
              console.log("useAuth: Handling UserNotFoundError...");
              setUserAccountError({
                type: "notFound",
                email:
                  currentAccount?.username || currentAccount?.name || undefined,
              });
              setError(null);
              setIsLoading(false);
              setAuthFlowCompleted(true);
              console.log(
                "useAuth: UserNotFoundError handled, loading set to false"
              );
              return;
            }

            if (error.name === "UserInactiveError") {
              console.log("useAuth: Handling UserInactiveError...");
              setUserAccountError({
                type: "inactive",
                email:
                  currentAccount?.username || currentAccount?.name || undefined,
              });
              setError(null);
              setIsLoading(false);
              setAuthFlowCompleted(true);
              console.log(
                "useAuth: UserInactiveError handled, loading set to false"
              );
              return;
            }

            // Check if it's a network/connectivity issue
            const isNetworkError =
              error instanceof Error &&
              (error.name === "NetworkError" ||
                error.message.includes("fetch") ||
                error.message.includes("network") ||
                error.message.includes("unreachable") ||
                error.message.includes("INTERNET_DISCONNECTED") ||
                error.message.includes("connectivity"));

            if (isNetworkError) {
              // Set backend unavailable flag for network errors
              sessionStorage.setItem("backend-unavailable", "true");
              // Clear the error to prevent login redirect loop
              setError(null);
              setIsLoading(false);
              setAuthFlowCompleted(true); // Mark as completed even with network error
            } else {
              // For other backend errors, still allow access but log the issue
              console.error("Backend token exchange failed:", error);
              // Clear the error to prevent login redirect loop
              setError(null);
              setIsLoading(false);
              setAuthFlowCompleted(true); // Mark as completed even with backend error
            }

            // Don't throw error, allow user to continue with Azure authentication only
          });
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [
    isAuthenticated,
    backendToken,
    authFlowCompleted,
    getBackendToken,
    currentAccount?.name,
    currentAccount?.username,
  ]);

  // Always use redirect flow now (no popup support needed)
  const isPopupSupported = useCallback(() => {
    return false; // Always false since we only use redirect flow
  }, []);

  return {
    isAuthenticated,
    isAuthResolving,
    currentAccount,
    backendToken,
    isLoading,
    error,
    userAccountError,
    authFlowCompleted,
    login,
    logout,
    getBackendToken,
    clearError: () => setError(null),
    clearUserAccountError: () => setUserAccountError(null),
    isPopupSupported,
  };
};
